import { useCallback, useEffect, useState } from "react";
import { Briefcase, UserPlus, X, CheckCircle2, Clock, Ban, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

function assignmentToast(delivery, email) {
  if (delivery && delivery.delivered) {
    toast.success(`Attorney assigned — invite email delivered to ${email}.`);
  } else if (delivery && delivery.reason === "resend_not_configured") {
    toast.info("Attorney assigned. Email delivery isn't set up yet — share the sign-up link with them directly.");
  } else if (delivery && delivery.reason === "resend_send_failed") {
    toast.warning("Attorney assigned, but the invite email failed to send. You may want to reach out directly.");
  } else {
    toast.success("Attorney assigned. They'll see this case the moment they sign up with that email.");
  }
}

function InviteForm({ email, setEmail, note, setNote, submitting, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="mt-3 space-y-3 border-t pt-4" style={{ borderColor: "var(--border-stone)" }} data-testid="assign-attorney-form">
      <div>
        <label className="field-label">Attorney email</label>
        <input
          type="email"
          required
          className="field-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="attorney@lawfirm.com"
          data-testid="assign-attorney-email-input"
        />
      </div>
      <div>
        <label className="field-label">Note (optional)</label>
        <input
          className="field-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Please review my I-130A before I file"
          data-testid="assign-attorney-note-input"
          maxLength={500}
        />
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: "var(--text-secondary)" }}>
        <span>Attorney needs to sign up with this email to gain access.</span>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary text-xs"
          data-testid="assign-attorney-submit-btn"
        >
          {submitting ? "Sending…" : "Assign attorney"}
        </button>
      </div>
    </form>
  );
}

function AssignmentRow({ r, onRevoke }) {
  return (
    <li className="flex items-start justify-between gap-3 border rounded-lg px-3 py-2"
        style={{ borderColor: "var(--border-stone)" }}
        data-testid={`assign-attorney-row-${r.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
          <Mail className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--accent-copper)" }} />
          <span className="truncate">{r.attorney_email}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs mt-1" style={{ color: r.status === "accepted" ? "var(--success-text)" : "var(--text-secondary)" }}>
          {r.status === "accepted" ? (
            <><CheckCircle2 className="w-3 h-3" /> Active — attorney can view this case</>
          ) : (
            <><Clock className="w-3 h-3" /> Pending — waiting for attorney to sign up</>
          )}
        </div>
        {r.note && <div className="text-xs italic mt-1" style={{ color: "var(--text-secondary)" }}>"{r.note}"</div>}
      </div>
      <button
        type="button"
        onClick={() => onRevoke(r.id)}
        className="btn-ghost text-xs flex items-center gap-1"
        style={{ border: "1px solid var(--border-stone)" }}
        data-testid={`assign-attorney-revoke-${r.id}`}
      >
        <Ban className="w-3 h-3" /> Revoke
      </button>
    </li>
  );
}

/**
 * AssignAttorneyCard — invite a licensed attorney to co-manage this case
 * via their own attorney account. Attorney sees a read-only workspace at
 * /attorney (they still need to sign up with the invited email).
 */
export default function AssignAttorneyCard({ caseData }) {
  const caseId = caseData.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/attorney-assignments`);
      setRows(data);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const active = rows.filter((r) => r.status !== "revoked");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/assign-attorney`, {
        attorney_email: email.trim(),
        note: note.trim() || undefined,
      });
      assignmentToast(data && data.email_delivery, email.trim());
      setEmail("");
      setNote("");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally { setSubmitting(false); }
  };

  const revoke = async (aid) => {
     
    if (!window.confirm("Revoke this attorney's access to the case?")) return;
    try {
      await api.post(`/cases/${caseId}/attorney-assignments/${aid}/revoke`);
      toast.success("Attorney access revoked.");
      load();
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  return (
    <div className="card-surface p-5" data-testid="assign-attorney-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
               style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}>
            <Briefcase className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-display font-bold text-base" style={{ color: "var(--text-primary)" }}>
              Assign an attorney
            </h3>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
              Invite a licensed immigration attorney to co-review this case in their own read-only workspace.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="btn-primary text-xs flex items-center gap-1"
          data-testid="assign-attorney-toggle"
        >
          {open ? <X className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
          {open ? "Cancel" : "Invite"}
        </button>
      </div>

      {open && (
        <InviteForm email={email} setEmail={setEmail} note={note} setNote={setNote}
                    submitting={submitting} onSubmit={submit} />
      )}

      {!loading && active.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="assign-attorney-list">
          {active.map((r) => (
            <AssignmentRow key={r.id} r={r} onRevoke={revoke} />
          ))}
        </ul>
      )}
    </div>
  );
}
